
/**
 * The pure half of parsing a wire sheet into a `SheetHandleCore`
 * (`sheet-handle-core.ts`): the entry checked and restored, its rows
 * normalized and decomposed into the client's registries with the chrome
 * beside them, each row's metadata stored for the backend, and the raw sheet
 * the rows side hydrates. Holds no state and never loads the core (its seams
 * are a type-only import).
 */
import type { Option } from "@tento-lona/sheets";
import { OrderedTree } from "@tento-core/ordered-tree";
import { Rational } from "@tento-core/rational";
import { RowKey, SheetNetworkKey } from "@tento-lona/sheets";
import { RowTypeCatalog } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import {
  RowNode,
} from "../rows/row-node.ts";
import { RowLocalId } from "@tento-lona/sheets";
import {
  SystemRows,
  isBuiltinSheetKey,
  RowNetworkKey,
} from "@tento-lona/sheets";
import { AliasTemplateOps } from "@tento-lona/sheets/internal-sdk";
import { Json, type JsonObject } from "@tento-lona/sheets";
import { RowId } from "@tento-lona/sheets";
import { SheetRowMeta } from "../rows/sheet-row-meta.ts";
import { RowType } from "../rows/row-descriptor.ts";
import type {
  AliasTemplate,
  RawRow,
  RawSheet,
} from "@tento-lona/sheets";
import { restoreBuiltinSheetShape } from "./builtin-seeds.ts";
import type { EphemeralSheets } from "./ephemeral-spec.ts";
import type { SheetHandleDeps } from "./sheet-handle-core.ts";
import { SheetHandle } from "./sheet-handle.ts";

/**
 * The front half of a sheet parse, before the core exists: `data` refused
 * unless every entry field is stated, given back its builtin shape
 * (`restoreBuiltinSheetShape`), its rows normalized (alias data addresses
 * through the registered templates) and decomposed into `deps`' registries,
 * and the chrome (`SystemRows`) built and registered beside them. Gives the
 * restored entry, its rows, the chrome, and the unresolved tree they form.
 */
export function parseSheetInput(
  data: SheetHandle.ParseInput,
  deps: Pick<SheetHandleDeps, "ids" | "rows" | "modelCache">,
  opts: SheetHandle.ParseConfig,
  /**
   * Given the rows this parse registered when it throws after registering
   * them (`RowNode.decompose`, the system rows): no core exists to release
   * them.
   */
  abandoned: Option<(rows: Set<RowLocalId>) => void> = null,
) {
  requireStatedSheet(data);
  // Builtin restoration happens HERE, at the parse boundary, because parse is
  // the one seam every ingestion path crosses: `sheets.open(key)`, the boot
  // `initial_payload` (`PayloadController.initFirstPayload` →
  // `SheetViewModel.make(payload.sheet)`), and any later re-`make` from a raw
  // wire sheet. Doing it only inside `open` meant a COLD LOAD parsed the
  // promoted Journal straight off the payload with no repair — its row came
  // back at the default height and the view config resolved against a null
  // lookup key — while a client-side navigation through `open` looked fine.
  // Shape only — never row seeding, which stays with the key-materializing
  // `open` path (see `withSeededReservedRows`). Idempotent, so `open`'s own
  // call (which additionally supplies the key the caller opened with) stays
  // correct.
  const parsed = restoreBuiltinSheetShape(data, null);
  const { rows, ...sheet } = parsed;
  // Template resolver for generic data-address normalization — the
  // rows accessor's registered builtin alias factories are the single
  // producers of alias data keys (data-address migration Phase B).
  const templateFor = (name: string): Option<AliasTemplate> =>
    deps.modelCache.aliasTemplate(name);
  const normalizedRows = rows.map((row) => normalizeParseRow(row, templateFor));
  const systemRowIds = new Set<RowLocalId>();
  // The part that registers rows: the parsed rows' tree and the system rows (`systemRowIds` collects these).
  const register = () => {
    const flattened = RowNode.decompose(normalizedRows, deps.ids, deps.rows);

    // Build and decompose system rows into the same tree structure.
    // The reserved/builtin lookup key rides on the sheet entry (restored
    // just above for builtins, set by `openEphemeralHandle` for catalog
    // sheets).
    const sheetLookupKey = parsed.lookupKey;
    const { rows: systemRows } = SystemRows.build(
      {
        sheet: parsed,
        debugRows: opts.debugRows,
        mode: opts.mode,
        catalog: isBuiltinSheetKey(sheetLookupKey) ? "builtin" : "user",
        registries: deps.modelCache.registries,
      },
      opts.factory ?? null,
    );
    for (const srow of systemRows) {
      const localId = deps.ids.resolve(RowKey.of(srow.id.toString()));
      systemRowIds.add(localId);

      deps.rows.set(localId, srow, null);
      flattened.positions.set(localId, srow.position);
    }

    // Footer row membership is structural: every descendant of the
    // footer surface-container is in the footer surface. Walk from
    // the footer surface-container's children recursively.
    const footerRowIds = SheetHandle.collectFooterRowIds(systemRows, deps.ids);

    const inner = OrderedTree.unflatten(flattened, Rational.compare);
    const unresolved = new RowNode.Tree(inner, deps.rows);
    return { parsed, sheet, normalizedRows, systemRows, systemRowIds, footerRowIds, unresolved };
  };
  try {
    return register();
  } catch (err) {
    if (abandoned != null) {
      const registered = new Set(systemRowIds);
      for (const row of normalizedRows) registered.add(deps.ids.resolve(RowKey.of(row.id.toString())));
      abandoned(registered);
    }
    throw err;
  }
}

/** What `parseSheetInput` gives the core it parses. */
export type ParsedSheetInput = ReturnType<typeof parseSheetInput>;

/** Store each parsed row's metadata in `backend`, detached, so its index factories can wire. */
export function storeParsedRowMeta(
  backend: SheetHandleDeps["backend"],
  rows: ParsedSheetInput["normalizedRows"],
): void {
  for (const r of rows) {
    const id = RowId.of(r.id.toString());
    // `SheetRow.Input`'s optional fields admit `undefined` (the ambient
    // `Optional`); `of` reads an omitted field as `null`, so say so.
    const db = Network.SheetRowDb.make({
      ...r,
      id,
      label: r.label ?? null,
      lookupKey: r.lookupKey ?? null,
      formula: r.formula ?? null,
      heightPx: r.heightPx ?? null,
      icon: r.icon ?? null,
      attributes: r.attributes ?? null,
      metadata: r.metadata ?? null,
    });
    backend.storeRowMeta(id, SheetRowMeta.of(db, SheetRowMeta.DETACHED), RowType.get(db.type).cellCardinality);
  }
}

/** Every field of the sheet entry `parseSheet` requires (`Network.UserSheet`). */
const USER_SHEET_FIELDS: readonly (keyof Network.UserSheet)[] = [
  "createdAt",
  "creatorId",
  "description",
  "id",
  "lastUpdatedAt",
  "lookupKey",
  "metadata",
  "position",
  "role",
  "timeScale",
  "title",
  "width",
];

/**
 * Refuse a sheet entry that leaves a field out. The type already requires
 * every field; this is the runtime half, for a caller the type did not
 * reach (a JavaScript host, a decoded payload), which used to get a handle
 * whose `network.createdAt` / `creatorId` / `role` read `undefined`.
 */
function requireStatedSheet(data: SheetHandle.ParseInput): void {
  const missing = USER_SHEET_FIELDS.filter((field) => data[field] === undefined);
  if (missing.length > 0) {
    throw new TypeError(
      "parseSheet: the sheet entry must state every field (null for none); missing: " +
        missing.join(", "),
    );
  }
}

/**
 * Parse-boundary normalization (data-address migration Phase B):
 *
 * 1. Canonicalize the wire type to a self-describing form. Resolve the
 *    alias template name from whatever shape arrived (legacy bare
 *    `"weather"`, a direct `"alias.<x>"`, or the generic `"alias"` +
 *    `attributes.template`) and, when it is a catalog alias, put the
 *    concrete `alias.<x>` in the `type` slot (round-trips via the direct
 *    parser builder). Non-catalog templates keep the generic `"alias"` +
 *    `attributes.template`. `attributes.template` is always populated.
 * 2. Generic data-address resolution: for any alias row whose
 *    registered template declares a `dataRef`, resolve the row's props
 *    (`AliasTemplateOps.resolveInitialProps` — which lifts legacy
 *    location info via `applyLegacyAliasProps`) and set the row's
 *    `lookupKey` to the resolved explicit data key. The template is the
 *    single producer; both the legacy registry and the rows-side sheet
 *    (hydrated from these normalized rows) then agree on ONE key.
 *
 * When the template is not registered on the attached rows accessor
 * (e.g. `alias.default-weather` on a bare SDK client without
 * `CalendarPreferences`), the row keeps its wire `lookupKey` and the
 * server resolves the alias identity — graceful degradation.
 */
function normalizeParseRow(
  row: RowNode.DecomposableRow,
  templateFor: (name: string) => Option<AliasTemplate>,
): RowNode.DecomposableRow {
  const templateName = aliasTemplateName(row);
  if (templateName == null) return row;

  const rawAttributes = row.attributes;
  const attributes = rawAttributes != null && isRecord(rawAttributes)
    ? rawAttributes
    : {};

  // A catalog alias emits its concrete `alias.<x>` type (self-describing,
  // round-trips via the direct parser builder); non-catalog templates keep
  // the generic `"alias"` + `attributes.template`.
  const type = templateName !== "alias" && RowTypeCatalog.contains(templateName)
    ? templateName
    : "alias";

  const normalized: RowNode.DecomposableRow = {
    ...row,
    type,
    attributes: { ...attributes, template: templateName },
  };
  const normalizedAttributes = Json.object(normalized.attributes ?? null) ?? {};

  const template = templateFor(templateName);
  if (template?.dataRef === undefined) return normalized;

  const props = AliasTemplateOps.resolveInitialProps(
    templateName,
    normalizedAttributes,
    {
      id: normalized.id.toString(),
      type: normalized.type,
      ...(normalized.lookupKey != null
        ? { lookupKey: normalized.lookupKey }
        : {}),
      attributes: normalizedAttributes,
    },
  );
  const ref = template.dataRef(props);
  if (ref.lookupKey.length === 0) return normalized;
  return {
    ...normalized,
    lookupKey: ref.lookupKey,
    attributes: {
      ...normalizedAttributes,
      props,
    },
  };
}

/**
 * The canonical alias template name for a wire row, or null when the row is
 * not an alias. Bridges the legacy bare `"weather"` kind and accepts both the
 * direct `"alias.<x>"` and the generic `"alias"` + `attributes.template`
 * shapes.
 */
function aliasTemplateName(row: RowNode.DecomposableRow): Option<string> {
  if (row.type === "weather") return "alias.weather";
  if (row.type.startsWith("alias.")) return row.type;
  if (row.type !== "alias") return null;
  const rawAttributes = Json.object(row.attributes ?? null);
  const attributes = rawAttributes != null && isRecord(rawAttributes)
    ? rawAttributes
    : {};
  const templateName = attributes.template;
  return typeof templateName === "string" && templateName.length > 0
    ? templateName
    : null;
}

function isRecord(value: Option<Json>): value is JsonObject {
  return Json.isObject(value);
}

/**
 * Build a tento/tento-lona-js/sheets `RawSheet` from `SheetHandle.ParseInput`.
 * Used by `SheetHandleCore`'s parse to hydrate the handle's rows-side sheet
 * through `deps.modelCache`. Mirrors the fields
 * both shapes share; seed cell data / position / ownerId aren't
 * part of `RawRow`.
 */
export function parseInputToRawSheet(data: SheetHandle.ParseInput): RawSheet {
  const { rows, ...sheetFields } = data;
  const rawRows: RawRow[] = rows.map((row) => {
    const id = RowNetworkKey.fromWire(row.id.toString());
    requireNonEmptyString(row.type, `parseInputToRawSheet row ${id} type`);
    const children = row.children?.map((c) => RowNetworkKey.fromWire(c.toString()));
    return {
      id,
      type: row.type,
      ...(row.label != null ? { label: row.label } : {}),
      ...(row.lookupKey != null ? { lookupKey: row.lookupKey } : {}),
      ...(row.formula != null ? { formula: row.formula } : {}),
      ...(row.timeScale != null ? { timeScale: row.timeScale } : {}),
      ...(row.heightPx != null ? { heightPx: row.heightPx } : {}),
      ...(Json.object(row.metadata ?? null) != null
        ? { meta: Json.object(row.metadata ?? null) ?? {} }
        : {}),
      ...(Json.object(row.attributes ?? null) != null
        ? { attributes: Json.object(row.attributes ?? null) ?? {} }
        : {}),
      ...(row.cells != null && row.cells.length > 0
        ? {
          cells: row.cells.map((cell) => ({
            date: cell.date,
            value: cell.data ?? null,
            ...(cell.id != null ? { cellId: cell.id } : {}),
          })),
        }
        : {}),
      ...(children != null && children.length > 0 ? { children } : {}),
    };
  });
  return {
    id: SheetNetworkKey.fromWire(data.id),
    rows: rawRows,
    ...(sheetFields.title != null ? { title: sheetFields.title } : {}),
    // The reserved sheet key rides across: a promoted builtin is installed in
    // the rows accessor under its SERVER id, so this is the only thing left
    // that still says ":journal" once the ephemeral id is gone.
    ...(sheetFields.lookupKey != null
      ? { lookupKey: sheetFields.lookupKey }
      : {}),
    ...(sheetFields.width != null ? { width: sheetFields.width } : {}),
    accessRole: sheetFields.role,
  };
}

export function requireNonEmptyString(value: string, label: string): void {
  if (value.length > 0) return;
  throw new Error(`${label} must be a non-empty string.`);
}

/**
 * Convert a lona-js `EphemeralSheet` to the tento/tento-lona-js/sheets
 * `RawSheet` shape so `SheetModelCache.openEphemeral` can hydrate
 * it. Maps the bits both shapes have in common; ephemeral-only
 * extras (`creatorId`, `description`) are dropped. Seed `data`
 * is carried into `RawRow.cells` so the rows-side mirror sees the
 * same in-memory template samples as the legacy `SheetHandle` path.
 */
export function toRawSheet(ephemeral: EphemeralSheets.EphemeralSheet): RawSheet {
  const rows: RawRow[] = ephemeral.rows.map((row) => {
    requireNonEmptyString(row.type, `toRawSheet row ${row.id.toString()} type`);
    const children = row.children?.map((c) => RowNetworkKey.of(c));
    return {
      id: RowNetworkKey.of(row.id),
      type: row.type,
      ...(row.label != null ? { label: row.label } : {}),
      ...(row.lookupKey != null ? { lookupKey: row.lookupKey } : {}),
      ...(row.formula != null ? { formula: row.formula } : {}),
      ...(row.timeScale != null ? { timeScale: row.timeScale } : {}),
      ...(row.heightPx != null ? { heightPx: row.heightPx } : {}),
      ...(Json.object(row.attributes ?? null) != null
        ? { attributes: Json.object(row.attributes ?? null) ?? {} }
        : {}),
      ...(row.data != null && row.data.length > 0
        ? {
          cells: row.data.map(([date, value]) => ({
            date,
            value,
          })),
        }
        : {}),
      ...(children != null && children.length > 0 ? { children } : {}),
    };
  });
  return {
    id: ephemeral.id,
    ...(ephemeral.title != null ? { title: ephemeral.title } : {}),
    ...(ephemeral.lookupKey != null
      ? { lookupKey: ephemeral.lookupKey }
      : {}),
    ...(ephemeral.width != null ? { width: ephemeral.width } : {}),
    ...(ephemeral.role != null ? { accessRole: ephemeral.role } : {}),
    rows,
  };
}
